import { useState } from "react";
import {
  formatWavePresenceDiagnostics,
  formatWaveSyncDiagnostics,
  type WavePresenceDiagnostics,
  type WaveSyncDiagnostics,
} from "@vida/wave-core";

type Props = {
  diagnostics: WaveSyncDiagnostics;
  waveTitle?: string;
  presence?: WavePresenceDiagnostics | null;
};

function statusClass(d: WaveSyncDiagnostics): string {
  if (d.networkHydrating) return "is-loading";
  if (d.seqGap > 0 || d.lastTailOpsFailed > 0) return "is-warning";
  if (d.appliedSeq >= d.expectedLastSeq && d.expectedLastSeq > 0) return "is-ok";
  return "";
}

export function WaveSyncDiagnosticsBar({ diagnostics: d, waveTitle, presence }: Props) {
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem("wave.syncDebugExpanded") === "1";
    } catch {
      return false;
    }
  });

  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("wave.syncDebugExpanded", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const copyDiagnostics = () => {
    const header = waveTitle ? `# ${waveTitle}\n` : "";
    const presenceBlock = presence
      ? `\n\n## Presenza\n${formatWavePresenceDiagnostics(presence)}`
      : "";
    void navigator.clipboard.writeText(
      `${header}${formatWaveSyncDiagnostics(d)}${presenceBlock}`,
    );
  };

  const summary = d.networkHydrating
    ? "Sync in corso…"
    : d.seqGap > 0
      ? `Mancano ${d.seqGap} seq`
      : "Seq allineate";

  return (
    <div
      className={`wave-sync-diagnostics ${statusClass(d)}`}
      role="region"
      aria-label="Diagnostica sync wave"
    >
      <button
        type="button"
        className="wave-sync-diagnostics-summary"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        title="Mostra dettaglio sequenze e ops caricate"
      >
        <span className="wave-sync-diagnostics-label">Sync</span>
        <span className="wave-sync-diagnostics-metric">
          seq <strong>{d.appliedSeq}</strong>/{d.expectedLastSeq}
        </span>
        <span className="wave-sync-diagnostics-metric">
          snap <strong>{d.waveSnapshotSeq}</strong>
        </span>
        <span className="wave-sync-diagnostics-metric">
          next <strong>{d.waveNextOpSeq}</strong>
        </span>
        <span className="wave-sync-diagnostics-metric">
          tail <strong>{d.lastTailOpsFetched}</strong>
          {d.lastTailOpsApplied > 0 ? ` (+${d.lastTailOpsApplied})` : ""}
        </span>
        <span className="wave-sync-diagnostics-metric">
          blocchi <strong>{d.blocksCount}</strong>
        </span>
        <span className={`wave-sync-diagnostics-status ${statusClass(d)}`}>
          {summary}
        </span>
      </button>
      {expanded && (
        <div className="wave-sync-diagnostics-detail">
          <dl className="wave-sync-diagnostics-grid">
            <div>
              <dt>Snapshot seq (server)</dt>
              <dd>{d.waveSnapshotSeq}</dd>
            </div>
            <div>
              <dt>Next op seq (server)</dt>
              <dd>{d.waveNextOpSeq}</dd>
            </div>
            <div>
              <dt>Ultima seq attesa</dt>
              <dd>{d.expectedLastSeq}</dd>
            </div>
            <div>
              <dt>Seq applicata (client)</dt>
              <dd>{d.appliedSeq}</dd>
            </div>
            <div>
              <dt>Gap seq</dt>
              <dd className={d.seqGap > 0 ? "is-warn" : undefined}>{d.seqGap}</dd>
            </div>
            <div>
              <dt>Tail richiesto dopo seq</dt>
              <dd>{d.tailRequestedAfterSeq}</dd>
            </div>
            <div>
              <dt>Ops tail scaricate</dt>
              <dd>{d.lastTailOpsFetched}</dd>
            </div>
            <div>
              <dt>Ops tail applicate (Yjs)</dt>
              <dd>{d.lastTailOpsApplied}</dd>
            </div>
            <div>
              <dt>Ops tail saltate (legacy)</dt>
              <dd>{d.lastTailOpsSkippedLegacy}</dd>
            </div>
            <div>
              <dt>Ops tail fallite</dt>
              <dd className={d.lastTailOpsFailed > 0 ? "is-warn" : undefined}>
                {d.lastTailOpsFailed}
              </dd>
            </div>
            <div>
              <dt>Blocchi materializzati</dt>
              <dd>{d.blocksCount}</dd>
            </div>
            <div>
              <dt>Cache locale</dt>
              <dd>
                {d.cacheApplied
                  ? `applicata (seq ${d.cacheAppliedSeq ?? "?"})`
                  : d.cacheRejectedStale
                    ? "rifiutata (stale)"
                    : "assente"}
              </dd>
            </div>
            <div>
              <dt>Fase</dt>
              <dd>{d.lastPhase}</dd>
            </div>
            <div>
              <dt>Reconcile bytes</dt>
              <dd>{d.lastReconcileUpdateBytes}</dd>
            </div>
            <div>
              <dt>Rete</dt>
              <dd>{d.networkHydrating ? "hydrating" : "idle"}</dd>
            </div>
            {presence && (
              <>
                <div>
                  <dt>Partecipanti (wave)</dt>
                  <dd>{presence.participantsCount}</dd>
                </div>
                <div>
                  <dt>Presenze live</dt>
                  <dd>{presence.presencesCount}</dd>
                </div>
                <div>
                  <dt>Last seen</dt>
                  <dd>{presence.lastSeenCount}</dd>
                </div>
                <div>
                  <dt>Roster UI</dt>
                  <dd>{presence.rosterCount}</dd>
                </div>
                <div>
                  <dt>Online (roster)</dt>
                  <dd>{presence.onlineCount}</dd>
                </div>
                <div>
                  <dt>Sub corrente</dt>
                  <dd className="wave-sync-diagnostics-sub">{presence.currentSub}</dd>
                </div>
                <div>
                  <dt>Realtime WS</dt>
                  <dd>{presence.realtimeConnected ? "connesso" : "no"}</dd>
                </div>
                <div>
                  <dt>Presenza poll-only</dt>
                  <dd>{presence.presencePollingOnly ? "sì" : "no"}</dd>
                </div>
                <div>
                  <dt>Ultimo errore presenza</dt>
                  <dd className={presence.lastError ? "is-warn" : undefined}>
                    {presence.lastError ?? "—"}
                  </dd>
                </div>
              </>
            )}
          </dl>
          <p className="wave-sync-diagnostics-hint">
            Apri la console del browser per i log <code>[Wave Yjs]</code>. Clicca
            «Copia» per incollare questo riepilogo in chat o issue.
          </p>
          <button type="button" className="wave-header-pill" onClick={copyDiagnostics}>
            Copia diagnostica
          </button>
        </div>
      )}
    </div>
  );
}
